import { Grid } from '@servicetitan/anvil2';
import { observer } from 'mobx-react';
import { FC, Fragment, ReactNode, Ref } from 'react';

interface PortalLayoutGridProps {
    announcementBanner?: ReactNode;
    alertBanner?: ReactNode;
    header?: ReactNode;
    sideNav?: ReactNode;
    mainContent?: ReactNode;
    logoInHeader: boolean;
    showSidebar: boolean;
    headerRowHeight?: string;
    mainContentRef?: Ref<HTMLDivElement>;
}

export const PortalLayoutGrid: FC<PortalLayoutGridProps> = observer(
    ({
        alertBanner,
        announcementBanner,
        header,
        headerRowHeight = '68px',
        logoInHeader,
        mainContent,
        mainContentRef,
        showSidebar,
        sideNav,
    }) => {
        const withAnnouncement = (layout: ReactNode) => (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100vh',
                    overflow: 'hidden',
                }}
            >
                {announcementBanner}
                <div style={{ flex: '1 1 auto', minHeight: 0 }}>{layout}</div>
            </div>
        );

        /*
         * Mobile mode: single column layout without sidebar space
         * Sidebar is rendered outside the grid as an overlay
         */
        if (!showSidebar) {
            return withAnnouncement(
                <Fragment>
                    <Grid
                        templateColumns="1fr"
                        templateRows={`${headerRowHeight} minmax(0, 1fr)`}
                        style={{ height: '100%', overflow: 'hidden' }}
                    >
                        <Grid
                            gridColumn="1"
                            gridRow="1"
                            style={{ borderBottom: '1px solid #e0e0e0' }}
                        >
                            {header}
                        </Grid>
                        <Grid gridColumn="1" gridRow="2" templateColumns="minmax(0, 1fr)">
                            <div ref={mainContentRef} style={{ overflowY: 'auto', height: '100%' }}>
                                {alertBanner}
                                {mainContent}
                            </div>
                        </Grid>
                    </Grid>
                    {/* Render sidebar outside grid so it can overlay */}
                    {sideNav}
                </Fragment>
            );
        }

        if (logoInHeader) {
            // Logo in header layout: header spans full width, sidebar on left
            return withAnnouncement(
                <Grid
                    templateColumns="240px 1fr"
                    templateRows="68px 1fr"
                    style={{ height: '100%', overflow: 'hidden' }}
                >
                    <Grid
                        gridColumn="1 / 3"
                        gridRow="1"
                        style={{ borderBottom: '1px solid #e0e0e0' }}
                    >
                        {header}
                    </Grid>
                    <Grid
                        gridColumn="1"
                        gridRow="2"
                        style={{ overflowY: 'auto', borderRight: '1px solid #e0e0e0' }}
                    >
                        {sideNav}
                    </Grid>
                    <Grid
                        gridColumn="2"
                        gridRow="2"
                        templateColumns="minmax(0, 1fr)"
                        templateRows="min-content 1fr"
                        style={{ overflowY: 'auto' }}
                    >
                        {alertBanner}
                        {mainContent}
                    </Grid>
                </Grid>
            );
        }

        // Logo in sidebar layout: sidebar spans full height, header on right
        return withAnnouncement(
            <Grid
                templateColumns="240px 1fr"
                templateRows="68px 1fr"
                style={{ height: '100%', overflow: 'hidden' }}
            >
                <Grid gridColumn="2 / 3" gridRow="1" style={{ borderBottom: '1px solid #e0e0e0' }}>
                    {header}
                </Grid>
                <Grid
                    gridColumn="1"
                    gridRow="1 / 3"
                    style={{ overflowY: 'auto', borderRight: '1px solid #e0e0e0' }}
                >
                    {sideNav}
                </Grid>
                <Grid
                    gridRow="2 / 3"
                    gridColumn="2"
                    templateColumns="minmax(0, 1fr)"
                    templateRows="min-content 1fr"
                    style={{ overflowY: 'auto' }}
                >
                    {alertBanner}
                    {mainContent}
                </Grid>
            </Grid>
        );
    }
);
